import React, { useState, useEffect, useMemo } from 'react';
import { 
  FiGrid, FiList, FiCalendar, FiFilter, FiArrowUp, FiArrowDown, FiSettings, 
  FiSave, FiEye, FiEdit, FiTrash2, FiShare2, FiPlus, FiSearch,
  FiColumns, FiDownload, FiRefreshCw, FiMoreVertical, FiUpload
} from 'react-icons/fi';
import { MassImportDialog } from '../import/MassImportDialog';

interface Column {
  id: string;
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'custom';
  width: number;
  visible: boolean;
  sortable: boolean;
  filterable: boolean;
  position: number;
  format?: string;
  options?: string[]; // For select type
  customRenderer?: string; // JavaScript function as string
}

interface Filter {
  id: string;
  columnId: string;
  operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'gt' | 'lt' | 'between' | 'in';
  value: any;
  label?: string;
}

interface ListViewConfig {
  id?: string;
  name: string;
  module: string;
  viewType: 'table' | 'grid' | 'kanban' | 'calendar';
  columns: Column[];
  filters: Filter[];
  sorting: { columnId: string; direction: 'asc' | 'desc' }[];
  grouping?: { columnId: string; direction: 'asc' | 'desc' };
  pagination: { pageSize: number; showSizeOptions: boolean };
  isPublic: boolean;
  isDefault: boolean;
}

interface ListViewData {
  [key: string]: any;
}

const modules = [
  { value: 'sales', label: 'Sales' },
  { value: 'crm', label: 'CRM' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'hr', label: 'Human Resources' },
  { value: 'procurement', label: 'Procurement' }
];

const viewTypes = [
  { value: 'table', label: 'Table', icon: FiList },
  { value: 'grid', label: 'Grid', icon: FiGrid },
  { value: 'kanban', label: 'Kanban', icon: FiColumns },
  { value: 'calendar', label: 'Calendar', icon: FiCalendar }
];

const operators = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'gt', label: 'Greater than' },
  { value: 'lt', label: 'Less than' },
  { value: 'between', label: 'Between' },
  { value: 'in', label: 'In list' }
];

export const CustomListView: React.FC<{
  data: ListViewData[];
  onDataChange?: (data: ListViewData[]) => void;
  module: string;
  configurable?: boolean;
  savedViews?: ListViewConfig[];
  onSaveView?: (config: ListViewConfig) => void;
  onLoadView?: (viewId: string) => void;
}> = ({ 
  data = [], 
  onDataChange, 
  module, 
  configurable = true,
  savedViews = [],
  onSaveView,
  onLoadView
}) => {
  const [config, setConfig] = useState<ListViewConfig>({
    name: 'Default View',
    module,
    viewType: 'table',
    columns: [],
    filters: [],
    sorting: [],
    pagination: { pageSize: 25, showSizeOptions: true },
    isPublic: false,
    isDefault: false
  });

  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [activeTab, setActiveTab] = useState<'columns' | 'filters' | 'sorting' | 'settings'>('columns');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Initialize columns from data structure
  useEffect(() => {
    if (data.length > 0 && config.columns.length === 0) {
      const sampleRow = data[0];
      const autoColumns: Column[] = Object.keys(sampleRow).map((key, index) => ({
        id: `col_${key}`,
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
        type: inferColumnType(sampleRow[key]),
        width: 150,
        visible: index < 8, // Show first 8 columns by default
        sortable: true,
        filterable: true,
        position: index
      }));

      setConfig(prev => ({ ...prev, columns: autoColumns }));
    }
  }, [data]);

  const inferColumnType = (value: any): Column['type'] => {
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) return 'date';
    return 'text';
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = [...data];

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchLower)
        )
      );
    }

    // Apply filters
    config.filters.forEach(filter => {
      const column = config.columns.find(c => c.id === filter.columnId);
      if (!column) return;

      filtered = filtered.filter(row => {
        const value = row[column.key];
        const filterValue = filter.value;

        switch (filter.operator) {
          case 'equals':
            return value === filterValue;
          case 'contains':
            return String(value).toLowerCase().includes(String(filterValue).toLowerCase());
          case 'startsWith':
            return String(value).toLowerCase().startsWith(String(filterValue).toLowerCase());
          case 'endsWith':
            return String(value).toLowerCase().endsWith(String(filterValue).toLowerCase());
          case 'gt':
            return Number(value) > Number(filterValue);
          case 'lt':
            return Number(value) < Number(filterValue);
          case 'in':
            return Array.isArray(filterValue) && filterValue.includes(value);
          default:
            return true;
        }
      });
    });

    // Apply sorting
    config.sorting.forEach(sort => {
      const column = config.columns.find(c => c.id === sort.columnId);
      if (!column) return;

      filtered.sort((a, b) => {
        const aValue = a[column.key];
        const bValue = b[column.key];

        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        if (aValue > bValue) comparison = 1;

        return sort.direction === 'desc' ? -comparison : comparison;
      });
    });

    return filtered;
  }, [data, config.filters, config.sorting, searchTerm]);

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * config.pagination.pageSize;
    return processedData.slice(startIndex, startIndex + config.pagination.pageSize);
  }, [processedData, currentPage, config.pagination.pageSize]);

  const totalPages = Math.ceil(processedData.length / config.pagination.pageSize);

  const addColumn = () => {
    const newColumn: Column = {
      id: `col_${Date.now()}`,
      key: 'new_column',
      label: 'New Column',
      type: 'text',
      width: 150,
      visible: true,
      sortable: true,
      filterable: true,
      position: config.columns.length
    };

    setConfig(prev => ({
      ...prev,
      columns: [...prev.columns, newColumn]
    }));
  };

  const updateColumn = (columnId: string, updates: Partial<Column>) => {
    setConfig(prev => ({
      ...prev,
      columns: prev.columns.map(col =>
        col.id === columnId ? { ...col, ...updates } : col
      )
    }));
  };

  const deleteColumn = (columnId: string) => {
    setConfig(prev => ({
      ...prev,
      columns: prev.columns.filter(col => col.id !== columnId)
    }));
  };

  const addFilter = () => {
    const visibleColumns = config.columns.filter(c => c.visible && c.filterable);
    if (visibleColumns.length === 0) return;

    const newFilter: Filter = {
      id: `filter_${Date.now()}`,
      columnId: visibleColumns[0].id,
      operator: 'equals',
      value: ''
    };

    setConfig(prev => ({
      ...prev,
      filters: [...prev.filters, newFilter]
    }));
  };

  const updateFilter = (filterId: string, updates: Partial<Filter>) => {
    setConfig(prev => ({
      ...prev,
      filters: prev.filters.map(filter =>
        filter.id === filterId ? { ...filter, ...updates } : filter
      )
    }));
  };

  const deleteFilter = (filterId: string) => {
    setConfig(prev => ({
      ...prev,
      filters: prev.filters.filter(filter => filter.id !== filterId)
    }));
  };

  const toggleSort = (columnId: string) => {
    setConfig(prev => {
      const existingSort = prev.sorting.find(s => s.columnId === columnId);
      let newSorting;

      if (!existingSort) {
        newSorting = [...prev.sorting, { columnId, direction: 'asc' as const }];
      } else if (existingSort.direction === 'asc') {
        newSorting = prev.sorting.map(s =>
          s.columnId === columnId ? { ...s, direction: 'desc' as const } : s
        );
      } else {
        newSorting = prev.sorting.filter(s => s.columnId !== columnId);
      }

      return { ...prev, sorting: newSorting };
    });
  };

  const saveView = () => {
    if (onSaveView) {
      onSaveView(config);
    }
  };

  const loadView = (viewId: string) => {
    if (onLoadView) {
      onLoadView(viewId);
    }
  };

  const exportData = () => {
    const visibleColumns = config.columns.filter(c => c.visible);
    const headers = visibleColumns.map(c => c.label);
    const rows = processedData.map(row =>
      visibleColumns.map(col => row[col.key])
    );

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${config.name.replace(/\s+/g, '_')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Mass Import functionality
  const [showImportDialog, setShowImportDialog] = useState(false);

  const handleImportComplete = (result: any) => {
    console.log('Import completed:', result);
    // Refresh data or trigger parent component to reload
    if (onDataChange) {
      // In a real implementation, this would fetch updated data
      onDataChange(data);
    }
  };

  return (
    <div className="flex h-full bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-gray-900">{config.name}</h2>
              
              {/* View Type Selector */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {viewTypes.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      className={`p-2 rounded-md transition-colors ${
                        config.viewType === type.value
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      onClick={() => setConfig(prev => ({ ...prev, viewType: type.value as any }))}
                      title={type.label}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>

              {/* Saved Views */}
              {savedViews.length > 0 && (
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  onChange={(e) => e.target.value && loadView(e.target.value)}
                  value=""
                >
                  <option value="">Load View...</option>
                  {savedViews.map(view => (
                    <option key={view.id} value={view.id}>{view.name}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="flex items-center space-x-2">
              {/* Search */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Actions */}
              <button
                onClick={() => setShowImportDialog(true)}
                className="px-3 py-2 text-sm bg-green-100 hover:bg-green-200 
                         text-green-700 rounded-md transition-colors flex items-center"
              >
                <FiUpload className="w-4 h-4 mr-2" />
                Import
              </button>

              <button
                onClick={exportData}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 
                         text-gray-700 rounded-md transition-colors flex items-center"
              >
                <FiDownload className="w-4 h-4 mr-2" />
                Export
              </button>

              {configurable && (
                <>
                  <button
                    onClick={() => setShowConfigPanel(!showConfigPanel)}
                    className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 
                             text-gray-700 rounded-md transition-colors flex items-center"
                  >
                    <FiSettings className="w-4 h-4 mr-2" />
                    Configure
                  </button>
                  <button
                    onClick={saveView}
                    className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 
                             text-white rounded-md transition-colors flex items-center"
                  >
                    <FiSave className="w-4 h-4 mr-2" />
                    Save View
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Active Filters */}
          {config.filters.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {config.filters.map(filter => {
                const column = config.columns.find(c => c.id === filter.columnId);
                return (
                  <div
                    key={filter.id}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm 
                             bg-blue-100 text-blue-800"
                  >
                    {column?.label} {filter.operator} {String(filter.value)}
                    <button
                      onClick={() => deleteFilter(filter.id)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {config.viewType === 'table' && (
            <TableView
              config={config}
              data={paginatedData}
              onSort={toggleSort}
              selectedRows={selectedRows}
              onSelectRows={setSelectedRows}
            />
          )}
          {config.viewType === 'grid' && (
            <GridView config={config} data={paginatedData} />
          )}
          {config.viewType === 'kanban' && (
            <KanbanView config={config} data={paginatedData} />
          )}
          {config.viewType === 'calendar' && (
            <CalendarView config={config} data={paginatedData} />
          )}
        </div>

        {/* Pagination */}
        <div className="bg-white border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * config.pagination.pageSize) + 1} to{' '}
              {Math.min(currentPage * config.pagination.pageSize, processedData.length)} of{' '}
              {processedData.length} results
            </div>
            
            <div className="flex items-center space-x-2">
              {config.pagination.showSizeOptions && (
                <select
                  className="px-3 py-1 border border-gray-300 rounded text-sm"
                  value={config.pagination.pageSize}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    pagination: { ...prev.pagination, pageSize: parseInt(e.target.value) }
                  }))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              )}
              
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm 
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm 
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Panel */}
      {configurable && showConfigPanel && (
        <ConfigurationPanel
          config={config}
          onUpdateConfig={setConfig}
          activeTab={activeTab}
          onTabChange={(tab) => setActiveTab(tab as 'columns' | 'filters' | 'sorting' | 'settings')}
          onAddColumn={addColumn}
          onUpdateColumn={updateColumn}
          onDeleteColumn={deleteColumn}
          onAddFilter={addFilter}
          onUpdateFilter={updateFilter}
          onDeleteFilter={deleteFilter}
          onClose={() => setShowConfigPanel(false)}
        />
      )}

      {/* Mass Import Dialog */}
      <MassImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        entity={module as any}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

// Table View Component
const TableView: React.FC<{
  config: ListViewConfig;
  data: ListViewData[];
  onSort: (columnId: string) => void;
  selectedRows: string[];
  onSelectRows: (rows: string[]) => void;
}> = ({ config, data, onSort, selectedRows, onSelectRows }) => {
  const visibleColumns = config.columns.filter(c => c.visible).sort((a, b) => a.position - b.position);

  const formatValue = (value: any, column: Column) => {
    if (value === null || value === undefined) return '';
    
    switch (column.type) {
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'number':
        return Number(value).toLocaleString();
      case 'boolean':
        return value ? '✓' : '✗';
      default:
        return String(value);
    }
  };

  return (
    <div className="overflow-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="w-12 px-6 py-3">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={selectedRows.length === data.length && data.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    onSelectRows(data.map(row => row.id || row._id || String(Math.random())));
                  } else {
                    onSelectRows([]);
                  }
                }}
              />
            </th>
            {visibleColumns.map(column => {
              const sortConfig = config.sorting.find(s => s.columnId === column.id);
              return (
                <th
                  key={column.id}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  style={{ width: column.width }}
                  onClick={() => column.sortable && onSort(column.id)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {column.sortable && (
                      <FiArrowUp className={`w-4 h-4 ${sortConfig ? 'text-blue-600' : 'text-gray-400'}`} />
                    )}
                  </div>
                </th>
              );
            })}
            <th className="w-12 px-6 py-3">
              <FiMoreVertical className="w-4 h-4 text-gray-400" />
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row, index) => {
            const rowId = row.id || row._id || String(index);
            return (
              <tr key={rowId} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={selectedRows.includes(rowId)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onSelectRows([...selectedRows, rowId]);
                      } else {
                        onSelectRows(selectedRows.filter(id => id !== rowId));
                      }
                    }}
                  />
                </td>
                {visibleColumns.map(column => (
                  <td key={column.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatValue(row[column.key], column)}
                  </td>
                ))}
                <td className="px-6 py-4">
                  <button className="text-gray-400 hover:text-gray-600">
                    <FiMoreVertical className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Grid View Component
const GridView: React.FC<{
  config: ListViewConfig;
  data: ListViewData[];
}> = ({ config, data }) => {
  const visibleColumns = config.columns.filter(c => c.visible).slice(0, 6); // Show max 6 fields in grid

  return (
    <div className="p-6 overflow-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {data.map((row, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
            {visibleColumns.map(column => (
              <div key={column.id} className="mb-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  {column.label}
                </div>
                <div className="text-sm text-gray-900">
                  {row[column.key] || '-'}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// Kanban View Component
const KanbanView: React.FC<{
  config: ListViewConfig;
  data: ListViewData[];
}> = ({ config, data }) => {
  // Find a suitable status column for kanban
  const statusColumn = config.columns.find(c => 
    c.key.toLowerCase().includes('status') || 
    c.key.toLowerCase().includes('stage') ||
    c.type === 'select'
  );

  if (!statusColumn) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        No status column found for Kanban view. Add a status or stage column to use this view.
      </div>
    );
  }

  const statuses = Array.from(new Set(data.map(row => row[statusColumn.key]))).filter(Boolean);
  
  return (
    <div className="p-6 overflow-auto">
      <div className="flex space-x-6 min-h-full">
        {statuses.map(status => {
          const statusData = data.filter(row => row[statusColumn.key] === status);
          return (
            <div key={status} className="flex-shrink-0 w-80">
              <div className="bg-gray-100 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-4">
                  {status} ({statusData.length})
                </h3>
                <div className="space-y-3">
                  {statusData.map((row, index) => (
                    <div key={index} className="bg-white rounded-lg shadow-sm p-4">
                      {config.columns.filter(c => c.visible && c.id !== statusColumn.id).slice(0, 3).map(column => (
                        <div key={column.id} className="mb-2">
                          <div className="text-xs text-gray-500">{column.label}</div>
                          <div className="text-sm text-gray-900">{row[column.key] || '-'}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Calendar View Component
const CalendarView: React.FC<{
  config: ListViewConfig;
  data: ListViewData[];
}> = ({ config, data }) => {
  const dateColumn = config.columns.find(c => c.type === 'date');

  if (!dateColumn) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        No date column found for Calendar view. Add a date column to use this view.
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="text-center text-gray-500">
        Calendar view coming soon...
        <br />
        Will display data by {dateColumn.label}
      </div>
    </div>
  );
};

// Configuration Panel Component
const ConfigurationPanel: React.FC<{
  config: ListViewConfig;
  onUpdateConfig: (config: ListViewConfig) => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onAddColumn: () => void;
  onUpdateColumn: (id: string, updates: Partial<Column>) => void;
  onDeleteColumn: (id: string) => void;
  onAddFilter: () => void;
  onUpdateFilter: (id: string, updates: Partial<Filter>) => void;
  onDeleteFilter: (id: string) => void;
  onClose: () => void;
}> = ({ 
  config, 
  onUpdateConfig, 
  activeTab, 
  onTabChange,
  onAddColumn,
  onUpdateColumn,
  onDeleteColumn,
  onAddFilter,
  onUpdateFilter,
  onDeleteFilter,
  onClose 
}) => {
  const tabs = [
    { id: 'columns', label: 'Columns', icon: FiColumns },
    { id: 'filters', label: 'Filters', icon: FiFilter },
    { id: 'sorting', label: 'Sorting', icon: FiArrowUp },
    { id: 'settings', label: 'Settings', icon: FiSettings }
  ];

  return (
    <div className="w-96 bg-white border-l border-gray-200 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">View Configuration</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          ×
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => onTabChange(tab.id)}
              >
                <Icon className="w-4 h-4 mx-auto mb-1" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'columns' && (
          <ColumnsConfig
            columns={config.columns}
            onAddColumn={onAddColumn}
            onUpdateColumn={onUpdateColumn}
            onDeleteColumn={onDeleteColumn}
          />
        )}
        {activeTab === 'filters' && (
          <FiltersConfig
            filters={config.filters}
            columns={config.columns}
            onAddFilter={onAddFilter}
            onUpdateFilter={onUpdateFilter}
            onDeleteFilter={onDeleteFilter}
          />
        )}
        {activeTab === 'sorting' && (
          <SortingConfig config={config} onUpdateConfig={onUpdateConfig} />
        )}
        {activeTab === 'settings' && (
          <SettingsConfig config={config} onUpdateConfig={onUpdateConfig} />
        )}
      </div>
    </div>
  );
};

// Column Configuration Component
const ColumnsConfig: React.FC<{
  columns: Column[];
  onAddColumn: () => void;
  onUpdateColumn: (id: string, updates: Partial<Column>) => void;
  onDeleteColumn: (id: string) => void;
}> = ({ columns, onAddColumn, onUpdateColumn, onDeleteColumn }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Columns</h4>
        <button
          onClick={onAddColumn}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <FiPlus className="w-4 h-4 inline mr-1" />
          Add
        </button>
      </div>

      <div className="space-y-3">
        {columns.map(column => (
          <div key={column.id} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <input
                type="text"
                className="font-medium text-sm border-none p-0 focus:outline-none focus:ring-0"
                value={column.label}
                onChange={(e) => onUpdateColumn(column.id, { label: e.target.value })}
              />
              <button
                onClick={() => onDeleteColumn(column.id)}
                className="text-red-600 hover:text-red-800"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <label className="block text-gray-600 mb-1">Type</label>
                <select
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  value={column.type}
                  onChange={(e) => onUpdateColumn(column.id, { type: e.target.value as any })}
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="boolean">Boolean</option>
                  <option value="select">Select</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-600 mb-1">Width</label>
                <input
                  type="number"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  value={column.width}
                  onChange={(e) => onUpdateColumn(column.id, { width: parseInt(e.target.value) })}
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-4 mt-2">
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-1"
                  checked={column.visible}
                  onChange={(e) => onUpdateColumn(column.id, { visible: e.target.checked })}
                />
                Visible
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-1"
                  checked={column.sortable}
                  onChange={(e) => onUpdateColumn(column.id, { sortable: e.target.checked })}
                />
                Sortable
              </label>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  className="mr-1"
                  checked={column.filterable}
                  onChange={(e) => onUpdateColumn(column.id, { filterable: e.target.checked })}
                />
                Filterable
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Filter Configuration Component
const FiltersConfig: React.FC<{
  filters: Filter[];
  columns: Column[];
  onAddFilter: () => void;
  onUpdateFilter: (id: string, updates: Partial<Filter>) => void;
  onDeleteFilter: (id: string) => void;
}> = ({ filters, columns, onAddFilter, onUpdateFilter, onDeleteFilter }) => {
  const filterableColumns = columns.filter(c => c.filterable);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Filters</h4>
        <button
          onClick={onAddFilter}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          disabled={filterableColumns.length === 0}
        >
          <FiPlus className="w-4 h-4 inline mr-1" />
          Add
        </button>
      </div>

      <div className="space-y-3">
        {filters.map(filter => {
          const column = columns.find(c => c.id === filter.columnId);
          return (
            <div key={filter.id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">Filter</span>
                <button
                  onClick={() => onDeleteFilter(filter.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-2">
                <div>
                  <label className="block text-gray-600 mb-1 text-sm">Column</label>
                  <select
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    value={filter.columnId}
                    onChange={(e) => onUpdateFilter(filter.id, { columnId: e.target.value })}
                  >
                    {filterableColumns.map(col => (
                      <option key={col.id} value={col.id}>{col.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-gray-600 mb-1 text-sm">Operator</label>
                  <select
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    value={filter.operator}
                    onChange={(e) => onUpdateFilter(filter.id, { operator: e.target.value as any })}
                  >
                    {operators.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-gray-600 mb-1 text-sm">Value</label>
                  <input
                    type={column?.type === 'number' ? 'number' : 'text'}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    value={filter.value}
                    onChange={(e) => onUpdateFilter(filter.id, { value: e.target.value })}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Sorting Configuration Component
const SortingConfig: React.FC<{
  config: ListViewConfig;
  onUpdateConfig: (config: ListViewConfig) => void;
}> = ({ config, onUpdateConfig }) => {
  const sortableColumns = config.columns.filter(c => c.sortable);

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">Sorting</h4>
      
      <div className="space-y-2">
        {config.sorting.map((sort, index) => {
          const column = config.columns.find(c => c.id === sort.columnId);
          return (
            <div key={sort.columnId} className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">{index + 1}.</span>
              <select
                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                value={sort.columnId}
                onChange={(e) => {
                  const newSorting = [...config.sorting];
                  newSorting[index] = { ...sort, columnId: e.target.value };
                  onUpdateConfig({ ...config, sorting: newSorting });
                }}
              >
                {sortableColumns.map(col => (
                  <option key={col.id} value={col.id}>{col.label}</option>
                ))}
              </select>
              <select
                className="px-2 py-1 border border-gray-300 rounded text-sm"
                value={sort.direction}
                onChange={(e) => {
                  const newSorting = [...config.sorting];
                  newSorting[index] = { ...sort, direction: e.target.value as any };
                  onUpdateConfig({ ...config, sorting: newSorting });
                }}
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
              <button
                onClick={() => {
                  const newSorting = config.sorting.filter((_, i) => i !== index);
                  onUpdateConfig({ ...config, sorting: newSorting });
                }}
                className="text-red-600 hover:text-red-800"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Settings Configuration Component
const SettingsConfig: React.FC<{
  config: ListViewConfig;
  onUpdateConfig: (config: ListViewConfig) => void;
}> = ({ config, onUpdateConfig }) => {
  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">Settings</h4>
      
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            View Name
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            value={config.name}
            onChange={(e) => onUpdateConfig({ ...config, name: e.target.value })}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Module
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            value={config.module}
            onChange={(e) => onUpdateConfig({ ...config, module: e.target.value })}
          >
            {modules.map(module => (
              <option key={module.value} value={module.value}>{module.label}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Page Size
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            value={config.pagination.pageSize}
            onChange={(e) => onUpdateConfig({
              ...config,
              pagination: { ...config.pagination, pageSize: parseInt(e.target.value) }
            })}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        
        <div className="space-y-2">
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              className="mr-2"
              checked={config.pagination.showSizeOptions}
              onChange={(e) => onUpdateConfig({
                ...config,
                pagination: { ...config.pagination, showSizeOptions: e.target.checked }
              })}
            />
            Show page size options
          </label>
          
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              className="mr-2"
              checked={config.isPublic}
              onChange={(e) => onUpdateConfig({ ...config, isPublic: e.target.checked })}
            />
            Public view (visible to all users)
          </label>
          
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              className="mr-2"
              checked={config.isDefault}
              onChange={(e) => onUpdateConfig({ ...config, isDefault: e.target.checked })}
            />
            Set as default view
          </label>
        </div>
      </div>
    </div>
  );
};

export default CustomListView; 